/* eslint-disable @typescript-eslint/no-explicit-any */
import { apiFlickClient, contentfulFlickClient, rollbar } from "lib/api";
import { toKVPairString } from "lib/util";
import moment from "moment";

import {
  AddressType,
  BillEstimatePayload,
  BillEstimateResponse,
  CreateSignUpPayload,
  fetchProductPayload,
  GetSignUpResponse,
  SignUpResponse,
  UpdateSignUpPayload,
  WindcaveTransaction,
} from "./types";

export const validateAddress = async (payload: AddressType) => {
  const mapToQuery = {
    unit: payload.unit,
    number: payload.number,
    street: payload.street.replace(/\s+/g, "+"),
    suburb: payload.suburb,
    city: payload.city,
    postcode: payload.postcode,
    region: payload.region,
  };

  const params = toKVPairString(mapToQuery, "&");
  const response = await apiFlickClient.get<any[]>(
    `/public_website/supply_nodes?${params}`,
  );

  return response.data;
};

export const getSupplyNodeDetails = async (payload: string) => {
  const response = await apiFlickClient.get<any[]>(
    `/public_website/supply_nodes/${payload}`,
  );

  return response.data;
};

export const fetchBillingEstimate = async (payload: BillEstimatePayload) => {
  const consumptionParam = payload.consumption
    ? `&consumption_over_period=${payload.consumption}`
    : "";

  /**
   * Add tomorrow day to the date start from mid night
   * example:
   * if today is  March 31 2023, midnight of today is April 01 2023 => +1 day,
   * nextday of today midnight is April 02 2023 => +1 day
   */
  const startDate = moment().startOf("day").add(2, "days");
  const daysToAdd = payload.duration || 30;
  const endDate = moment()
    .endOf("day")
    .add(2, "days")
    .add(daysToAdd - 1, "days");

  // convert local times to UTC before making call
  const dateParams = `&start_at=${startDate.utc().format()}&end_at=${endDate
    .utc()
    .format()}`;

  const response = await apiFlickClient.get<BillEstimateResponse>(
    `/public_website/bill_estimate/${payload.icp}?brand=flick${consumptionParam}${dateParams}`,
  );

  return response.data;
};

export const getWindcaveAuthorisation = async (transactionId: string) => {
  const response = await apiFlickClient.get<WindcaveTransaction>(
    `/customer/credit_cards/authorisations/${transactionId}`,
  );
  return response.data;
};
export const createWindcaveTransaction = async (signUpId: string) => {
  const redirect_url = window.location.href;
  const response = await apiFlickClient.post<WindcaveTransaction>(
    `/customer/credit_cards/authorisations`,
    {
      redirect_url,
      brand: "flick",
      sign_up_id: signUpId,
    },
  );
  return response.data;
};

export const createUserFlickSignUp = async (payload: CreateSignUpPayload) => {
  const { addressDetails, creationSource, signUpState, promoCode } = payload;
  const town =
    addressDetails.city !== "" ? addressDetails.city : addressDetails.suburb;
  const mapToPayload = {
    state: signUpState,
    // Product
    brand: "flick",
    bundle_ref: "",
    billing_period: "fortnightly",
    billing_period_offset_weeks: 0,
    // Property/Address
    ea_icp_id: addressDetails.icp,
    promotional_code: promoCode,
    creation_source: creationSource,
    address: {
      town,
      unit: addressDetails.unit,
      street: addressDetails.street,
      number: addressDetails.number,
      suburb: addressDetails.suburb,
      post_code: addressDetails.postcode,
    },
  };

  const response = await apiFlickClient.post<SignUpResponse>(
    "/customer/nz/sign_ups",
    mapToPayload,
  );

  return response.data;
};

export const updateUserFlickSignUp = async (payload: UpdateSignUpPayload) => {
  const {
    details,
    payment,
    property,
    addressDetails,
    checks,
    id,
    creationSource,
    leadSource,
    signUpState,
    promoCode,
  } = payload;

  /*
  Errors have occurred where ID is lost from local state and or store. ID from local storage is used as a fallback
  */
  const localSignUpUUID = localStorage.getItem("signUpId");

  if (!Boolean(id) && !Boolean(localSignUpUUID)) {
    rollbar.error(
      `Error: Signup ID does not exist in state or local storage. ICP ${addressDetails.icp}`,
      JSON.stringify(payload),
    );
  }

  const town =
    addressDetails.city !== "" ? addressDetails.city : addressDetails.suburb;
  const mapToPayload = {
    id: id || localSignUpUUID,
    state: signUpState,
    // User
    first_name: details.firstName,
    last_name: details.lastName,
    preferred_name: details.prefName,
    company_name: details.companyName,
    email: details.emailAddress,
    mobile_number: details.mobileNumber,
    ...(details.homeNumber && { home_number: details.homeNumber }),
    birth_date: details.dob,
    // Product
    brand: "flick",
    product: details.product,
    bundle_ref: "",
    usage_type: property.usageType,
    // Payment
    payment_method: payment.paymentType,
    bank_account_type: payment.bankAccountType,
    billing_period: payment.billingPeriod,
    billing_period_offset_weeks: 0,
    credit_check_accepted: checks.creditCheckAccepted,
    terms_accepted: checks.termsAccepted,
    marketing_accepted: checks.marketingAccepted,
    switch_notifications_accepted: checks.smsSwitchNotificationsAccepted,
    direct_debit: {
      account_name: payment.directDebit?.accountName,
      account_number: payment.directDebit?.accountNumber,
      particulars: payment.directDebit?.pariculars,
      code: payment.directDebit?.code,
      terms_accepted: payment.directDebit?.tncsAgreed,
      authorised: payment.directDebit?.authorityConfirmed,
    },
    // Property/Address
    switch_date: property.switchDate,
    move_in_date: property.moveDate,
    medically_dependent: property.medicallyDependent,
    ea_icp_id: addressDetails.icp,
    promotional_code: promoCode,
    creation_source: creationSource,
    lead_source: leadSource,
    address: {
      town,
      unit: addressDetails.unit,
      street: addressDetails.street,
      number: addressDetails.number,
      suburb: addressDetails.suburb,
      post_code: addressDetails.postcode,
    },
  };

  const response = await apiFlickClient.put<SignUpResponse>(
    "/customer/nz/sign_ups",
    mapToPayload,
  );

  return response.data;
};

export const getUserFlickSignUp = async (payload: string) => {
  const response = await apiFlickClient.get<GetSignUpResponse>(
    `/customer/nz/sign_ups/${payload}`,
  );

  return response.data;
};

export const fetchProductData = async (payload: fetchProductPayload) => {
  const response = await contentfulFlickClient.post(
    `/content/v1/spaces/${process.env.REACT_APP_CONTENTFUL_SPACE_ID}/`,
    { query: JSON.parse(payload.query) },
  );
  return response.data;
};

export const verifyEmail = async (payload: string) => {
  const response = await apiFlickClient.get(
    `https://api.addressfinder.io/api/email/v1/verification/?key=${process.env.REACT_APP_ADDRESS_FINDER_KEY}&email=${payload}&format=json&features=domain%2Cconnection`,
  );

  return response.data;
};
